import React from "react";
import { Check, X, AlertCircle, Box as BoxIcon, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

// Lifecycle Status Options
export const LIFECYCLE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "limited_support", label: "Limited Support" },
  { value: "end_of_sale", label: "End of Sale" },
  { value: "end_of_support", label: "End of Support" },
  { value: "obsolete", label: "Obsolete" },
  { value: "decommissioned", label: "Decommissioned" },
];
export const FAQ_TYPE_OPTIONS = [
  { value: "product", label: "Product" },
  { value: "product_type", label: "Product Type" },
  { value: "product_category", label: "Product Category" },
  { value: "asset", label: "Asset" },
];
// Manufacturer Status Options
export const MANUFACTURER_STATUS_OPTIONS = [
  { value: "operational", label: "Operational" },
  { value: "discontinued", label: "Discontinued" },
  { value: "eol", label: "EOL" },
];

// Asset Status Options (matches AssetStatus enum)
export const ASSET_STATUS_OPTIONS = [
  { value: "in_review", label: "In Review" },
  { value: "active", label: "Active" },
  // { value: "inactive", label: "Inactive" },
  { value: "decommissioned", label: "Decommissioned" },
  // { value: "maintenance", label: "Maintenance" },
];

// Interval Unit Options (matches IntervalType enum)
export const INTERVAL_UNIT_OPTIONS = [
  { value: "daily", label: "Days" },
  { value: "weekly", label: "Weeks" },
  { value: "monthly", label: "Months" },
  { value: "yearly", label: "Years" },
];

export const getIntervalUnitLabel = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const option = INTERVAL_UNIT_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value;
};

// Schedule Type Options (matches ScheduleType enum)
export const SCHEDULE_TYPE_OPTIONS = [
  { value: "service_reminder", label: "Service reminder" },
  { value: "inspection", label: "Inspection" },
  { value: "tune_up", label: "Tune up" },
  { value: "calibration", label: "Calibration" },
];

// Work Order Service Category Options (matches WorkOrderServiceCategory enum)
export const WORK_ORDER_SERVICE_CATEGORY_OPTIONS = [
  { value: "hvac", label: "HVAC" },
  { value: "electrical", label: "Electrical" },
  { value: "plumbing", label: "Plumbing" },
  { value: "architectural", label: "Architectural" },
  { value: "computers_and_telecom", label: "Computers and Telecom" },
  { value: "fire_and_life_safety", label: "Fire and Life Safety" },
  { value: "landscape", label: "Landscape" },
  { value: "security", label: "Security" },
  { value: "structural", label: "Structural" },
  { value: "general", label: "General" },
];

// Work Order Type Options (matches WorkOrderType enum)
export const WORK_ORDER_TYPE_OPTIONS = [
  { value: "preventive_maintenance", label: "Preventive Maintenance" },
  { value: "corrective_maintenance", label: "Corrective Maintenance" },
  { value: "installation", label: "Installation" },
  { value: "emergency", label: "Emergency" },
];

// Work Order Business Severity Options (matches WorkOrderBusinessSeverity enum)
export const WORK_ORDER_BUSINESS_SEVERITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

// Work Order Priority Options (matches WorkOrderPriority enum)
export const WORK_ORDER_PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

// Work Order Assignment Type Options (matches WorkOrderAssignmentType enum)
export const WORK_ORDER_ASSIGNMENT_TYPE_OPTIONS = [
  // { value: "maintenance", label: "Maintenance" },
  // { value: "installation", label: "Installation" },
  // { value: "inspection", label: "Inspection" },
  { value: "responsible", label: "Responsible" },
  { value: "consulting", label: "Consulting" },
  { value: "accountable", label: "Accountable" },
  { value: "informing", label: "Informing" },
];
// Inspection Status Options (per-asset inspection result)
export const INSPECTION_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "in_review", label: "Review" },
];

// Inspection Asset Status Options
export const INSPECTION_ASSET_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_review", label: "In Review" },
  { value: "completed", label: "Completed" },
  { value: "approved", label: "Approved" },
];

// Helper function to get inspection status label
export const getInspectionStatusLabel = (enumValue: string | null | undefined): string => {
  if (enumValue === null || enumValue === undefined) return '';
  const option = INSPECTION_STATUS_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

// Helper function to get inspection status value from label
export const getInspectionStatusValue = (label: string): string => {
  const option = INSPECTION_STATUS_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

// Helper function to get inspection status badge className
export const getInspectionStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return "gray-badge";

  switch (status.toLowerCase()) {
    case 'pass':
      return 'green-badge';
    case 'fail':
      return 'red-badge';
    case 'in_review':
      return 'orange-badge';
    case 'open':
      return 'gray-badge';
    default:
      return 'gray-badge';
  }
};

// Helper function to get inspection status icon
export const getInspectionStatusIcon = (status: string | null | undefined): React.ReactElement | null => {
  if (!status) return null;

  switch (status.toLowerCase()) {
    case "pass":
      return React.createElement(Check, { size: 14, style: { marginRight: "4px" } });
    case "fail":
      return React.createElement(X, { size: 14, style: { marginRight: "4px" } });
    case "in_review":
      return React.createElement(AlertCircle, { size: 14, style: { marginRight: "4px" } });
    default:
      return null;
  }
};

// Helper function to get inspection asset status label
export const getInspectionAssetStatusLabel = (enumValue: string | null | undefined): string => {
  if (enumValue === null || enumValue === undefined) return '';
  const option = INSPECTION_ASSET_STATUS_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

// Helper function to get inspection asset status value from label
export const getInspectionAssetStatusValue = (label: string): string => {
  const option = INSPECTION_ASSET_STATUS_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

// Helper function to get inspection asset status badge className
export const getInspectionAssetStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return "gray-badge";

  switch (status.toLowerCase()) {
    case 'open':
      return 'gray-badge';
    case 'in_review':
      return 'orange-badge';
    case 'completed':
      return 'blue-badge';
    case 'approved':
      return 'green-badge';
    default:
      return 'gray-badge';
  }
};

/** Order-level status badge classes (list UI); keep separate from asset-status mapping. */
export const getInspectionOrderStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return "gray-badge";

  switch (status.toLowerCase()) {
    case "open":
      return "blue-badge";
    case "in_review":
      return "orange-badge";
    case "completed":
    case "approved":
      return "green-badge";
    default:
      return "gray-badge";
  }
};

export const getInspectionOrderStatusLabel = (status: string | null | undefined): string => {
  if (!status) return "";
  const option = INSPECTION_ASSET_STATUS_OPTIONS.find(
    (opt) => opt.value === status.toLowerCase(),
  );
  if (option) return option.label;
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// Helper functions to convert between enum values and display labels
export const getScheduleTypeLabel = (enumValue: string): string => {
  const option = SCHEDULE_TYPE_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getScheduleTypeValue = (label: string): string => {
  const option = SCHEDULE_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

// Helper function to get schedule type badge className
export const getScheduleTypeBadgeClass = (scheduleType: string | null | undefined): string => {
  if (!scheduleType) return "gray-badge";
  switch (scheduleType) {
    case 'tune_up':
      return 'orange-badge';
    case 'inspection':
      return 'green-badge';
    case 'calibration':
      return 'gray-badge';
    case 'service_reminder':
      return 'blue-badge';
    default:
      return 'gray-badge';
  }
};

// Helper function to get maintenance priority badge className (low, medium, high, critical)
export const getMaintenancePriorityBadgeClass = (priority: string | null | undefined): string => {
  if (!priority) return "gray-badge";
  switch (priority.toLowerCase()) {
    case 'low':
      return 'green-badge';
    case 'medium':
      return 'blue-badge';
    case 'high':
      return 'red-badge';
    case 'critical':
      return 'urgent-badge';
    default:
      return 'gray-badge';
  }
};

export const getWorkOrderServiceCategoryLabel = (enumValue: string): string => {
  const option = WORK_ORDER_SERVICE_CATEGORY_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getWorkOrderServiceCategoryValue = (label: string): string => {
  const option = WORK_ORDER_SERVICE_CATEGORY_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

export const getWorkOrderTypeLabel = (enumValue: string): string => {
  const option = WORK_ORDER_TYPE_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getWorkOrderTypeValue = (label: string): string => {
  const option = WORK_ORDER_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

export const getWorkOrderBusinessSeverityLabel = (enumValue: string): string => {
  const option = WORK_ORDER_BUSINESS_SEVERITY_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getWorkOrderBusinessSeverityValue = (label: string): string => {
  const option = WORK_ORDER_BUSINESS_SEVERITY_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

export const getWorkOrderPriorityLabel = (enumValue: string): string => {
  const option = WORK_ORDER_PRIORITY_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getWorkOrderPriorityValue = (label: string): string => {
  const option = WORK_ORDER_PRIORITY_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

export const getWorkOrderAssignmentTypeLabel = (enumValue: string | null | undefined): string => {
  if (enumValue === null || enumValue === undefined) return '';
  const option = WORK_ORDER_ASSIGNMENT_TYPE_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getWorkOrderAssignmentTypeValue = (label: string): string => {
  const option = WORK_ORDER_ASSIGNMENT_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

export const getLifecycleStatusLabel = (enumValue: string | null | undefined): string => {
  if (enumValue === null || enumValue === undefined) return '';
  const option = LIFECYCLE_STATUS_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getLifecycleStatusValue = (label: string): string => {
  const option = LIFECYCLE_STATUS_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

export const getManufacturerStatusLabel = (enumValue: string | null | undefined): string => {
  if (enumValue === null || enumValue === undefined) return '';
  const option = MANUFACTURER_STATUS_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getManufacturerStatusValue = (label: string): string => {
  const option = MANUFACTURER_STATUS_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

export const getAssetStatusLabel = (enumValue: string | null | undefined): string => {
  if (enumValue === null || enumValue === undefined) return '';
  const option = ASSET_STATUS_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getAssetStatusValue = (label: string): string => {
  const option = ASSET_STATUS_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

// Get badge class name based on asset status
export const getAssetStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return 'gray-badge';

  switch (status) {
    case 'in_review':
      return 'orange-badge';
    case 'active':
      return 'green-badge';
    case 'inactive':
      return 'gray-badge';
    case 'decommissioned':
      return 'red-badge';
    case 'maintenance':
      return 'blue-badge';
    default:
      return 'gray-badge';
  }
};
export const getFAQTypeLabel = (enumValue: string): string => {
  const option = FAQ_TYPE_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : enumValue;
};

export const getFAQTypeValue = (label: string): string => {
  const option = FAQ_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === label.toLowerCase());
  return option ? option.value : label;
};

// Helper function to get lifecycle status badge className
export const getLifecycleStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return "gray-badge";

  const statusMap: Record<string, string> = {
    "active": "green-badge",
    "limited_support": "orange-badge",
    "end_of_sale": "orange-badge",
    "end_of_support": "red-badge",
    "obsolete": "red-badge",
    "decommissioned": "gray-badge",
  };

  return statusMap[status.toLowerCase()] || "gray-badge";
};



// Helper function to get manufacturer status badge className
export const getManufacturerStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return "gray-badge";

  const statusMap: Record<string, string> = {
    "operational": "green-badge",
    "discontinued": "orange-badge",
    "eol": "red-badge",
  };

  return statusMap[status.toLowerCase()] || "gray-badge";
};

// Inspection Summary Card Configuration
export interface InspectionSummaryCardConfig {
  iconColor: string;
  iconBgColor: string;
  textColor: string;
  icon: React.ReactElement;
}

// Helper function to get inspection summary card configuration
export const getInspectionSummaryCardConfig = (type: 'total' | 'passed' | 'failed' | 'review' | 'pending'): InspectionSummaryCardConfig => {
  switch (type) {
    case 'total':
      return {
        iconColor: '#4285F4',
        iconBgColor: '#E8EAF6',
        textColor: '#1f2937',
        icon: React.createElement(BoxIcon, { size: 24 }),
      };
    case 'passed':
      return {
        iconColor: '#4CAF50',
        iconBgColor: '#E8F5E9',
        textColor: '#1f2937',
        icon: React.createElement(CheckCircle, { size: 24 }),
      };
    case 'failed':
      return {
        iconColor: '#F44336',
        iconBgColor: '#FFEBEE',
        textColor: '#1f2937',
        icon: React.createElement(XCircle, { size: 24 }),
      };
    case 'review':
      return {
        iconColor: '#FFC107',
        iconBgColor: '#FFF3E0',
        textColor: '#1f2937',
        icon: React.createElement(AlertCircle, { size: 24 }),
      };
    case 'pending':
      return {
        iconColor: '#64748b',
        iconBgColor: '#f1f5f9',
        textColor: '#1f2937',
        icon: React.createElement(BoxIcon, { size: 24 }),
      };

    default:
      return {
        iconColor: '#6b7280',
        iconBgColor: '#f3f4f6',
        textColor: '#1f2937',
        icon: React.createElement(BoxIcon, { size: 24 }),
      };
  }
};

// Document Type Options
export const DOCUMENT_TYPE_OPTIONS = [
  { value: "SOP", label: "SOP" },
];

// Content Source Options
export const CONTENT_SOURCE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "ai", label: "Generate with AI" },
  { value: "upload", label: "Upload File" },
];

// Document Source Constants
export const DOCUMENT_SOURCE = {
  WORK_ORDER: "work_order",
  INSPECTION: "inspection",
  ONBOARDING: "onboarding",
  INCIDENT: "incident",
  MANUAL_ASSETS: "manual_assets",
  MANUAL_LOCATIONS: "manual_locations",
  COMPANY_INFO: "company_info",
} as const;

export type DocumentSource = typeof DOCUMENT_SOURCE[keyof typeof DOCUMENT_SOURCE];
// Document Source Options for Filtering
export const DOCUMENT_SOURCE_OPTIONS = [
  { value: 'manual_assets', label: 'Assets' },
  { value: 'work_order', label: 'Work Orders' },
  { value: 'inspection', label: 'Inspections' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'incident', label: 'Incidents' },
  { value: 'manual_locations', label: 'Locations' },
  { value: 'company_info', label: 'Company Info' },
];

// Helper function to get document source label
export const getDocumentSourceLabel = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const option = DOCUMENT_SOURCE_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Document ETL / extraction status constants (filter + display)
export const DOCUMENT_STATUS = {
  QUEUED: "queued",
  PROCESSING: "processing",
  IN_REVIEW: "in_review",
  COMPLETE: "complete",
  FAILED: "failed",
} as const;

export type DocumentStatus = typeof DOCUMENT_STATUS[keyof typeof DOCUMENT_STATUS];

export const DOCUMENT_STATUS_OPTIONS = [
  { value: DOCUMENT_STATUS.QUEUED, label: "Queued" },
  { value: DOCUMENT_STATUS.PROCESSING, label: "Extracting" },
  { value: DOCUMENT_STATUS.IN_REVIEW, label: "In Review" },
  { value: DOCUMENT_STATUS.COMPLETE, label: "Complete" },
  { value: DOCUMENT_STATUS.FAILED, label: "Failed" },
] as const;

/** DB / legacy etl.status values that map to each filter option.
 * Note: `complete` without user_modified is treated as in_review by the API
 * (see normalizeEtlWorkflowStatus) — only user-confirmed complete matches Complete. */
export const DOCUMENT_STATUS_DB_VALUES: Record<DocumentStatus, readonly string[]> = {
  [DOCUMENT_STATUS.QUEUED]: ["queued", "pending"],
  [DOCUMENT_STATUS.PROCESSING]: ["processing"],
  [DOCUMENT_STATUS.IN_REVIEW]: ["in_review", "completed", "pending_user_review"],
  [DOCUMENT_STATUS.COMPLETE]: ["complete"],
  [DOCUMENT_STATUS.FAILED]: ["failed", "fail"],
};

export const getDocumentStatusLabel = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const option = DOCUMENT_STATUS_OPTIONS.find((opt) => opt.value === value);
  return option
    ? option.label
    : value.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

// Assistant event names for cross-component communication
export const ASSISTANT_LOCATION_UPDATE = 'assistantLocationUpdate';
export const ASSISTANT_NAVIGATE = 'assistantNavigate';
export const TOGGLE_FACILITY_CHAT = 'toggleFacilityChat';
